import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withEnabledBlockingInitialNavigation, withHashLocation, withInMemoryScrolling } from '@angular/router';
import Aura from '@primeng/themes/aura';
import { providePrimeNG } from 'primeng/config';
import { appRoutes } from './app.routes';
import { definePreset } from '@primeng/themes';
import { apiInterceptor } from '@/interceptors/api.interceptor';
import { ConfirmationService, MessageService } from 'primeng/api';
import { provideMonacoEditor } from 'ngx-monaco-editor-v2';

/**
 * PSA BDP theme preset.
 *
 * Colour values come from the "PSA BDP Poseidon UI Kit Branding Guidelines".
 * The guideline-mandated values are marked; the remaining ramp steps are
 * interpolations needed to give PrimeNG a complete 50–950 scale.
 *
 * The same values are mirrored as `--bdp-*` custom properties in
 * `assets/layout/variables/_bdp_brand.scss` for use outside PrimeNG.
 */
const BDP_PRIMARY = {
    50: '#eff5ff',
    100: '#dbe8ff',
    200: '#bfd5ff',
    300: '#6d9eff', // guideline — Bright Blue B (chart 7)
    400: '#4b87ff', // guideline — 3b
    500: '#206bff', // guideline — 3. Bright Blue, the brand primary
    600: '#004ce1', // guideline — 3c, hover / pressed
    700: '#0040bd',
    800: '#003396', // guideline — 2. Dark Bright Blue (chart 2)
    900: '#002a7a',
    950: '#001b4d'
};

/** Cool neutral ramp anchored on the guideline gray families. */
const BDP_SURFACE_LIGHT = {
    0: '#ffffff', // guideline — 4. White (cards & charts)
    50: '#f6f9fe', // guideline — 2. Light Gray
    100: '#e8f1f8', // guideline — 2b
    200: '#e2edf6', // guideline — 2c, highlighted gray areas
    300: '#cdd9e6',
    400: '#a3b3c7',
    500: '#7a8ca5',
    600: '#5c6f8a',
    700: '#3e5172', // guideline — 1b, hover
    800: '#2e3c54', // guideline — 1. Dark Gray, top bar
    900: '#2c3a52', // guideline — 1c, menu bar
    950: '#1c2637'
};

/**
 * Dark mode is not covered by the guidelines. It reuses the same hue family so
 * the two schemes read as one brand, with Bright Blue kept as the accent.
 */
const BDP_SURFACE_DARK = {
    0: '#ffffff',
    50: '#f5f5f6',
    100: '#e6e6e8',
    200: '#c9c9cd',
    300: '#a4a4aa',
    400: '#7e7e85',
    500: '#5d5d64',
    600: '#414147',
    700: '#2b2b30',
    800: '#1d1d21',
    900: '#151517',
    950: '#0a0a0b'
};

const MyPreset = definePreset(Aura, {
    semantic: {
        primary: BDP_PRIMARY,
        overlay: {
            modal: {
                borderRadius: '1.5rem'
            },
            popover: {
                borderRadius: '10px'
            }
        },
        colorScheme: {
            light: {
                primary: {
                    color: '{primary.500}', // #206bff
                    contrastColor: '#ffffff',
                    hoverColor: '{primary.600}', // #004ce1
                    activeColor: '{primary.600}'
                },
                highlight: {
                    background: '{primary.50}',
                    focusBackground: '{primary.100}',
                    color: '{primary.700}',
                    focusColor: '{primary.800}'
                },
                surface: BDP_SURFACE_LIGHT
            },
            dark: {
                primary: {
                    color: '{primary.400}', // #4b87ff reads better on dark
                    contrastColor: '#ffffff',
                    hoverColor: '{primary.300}',
                    activeColor: '{primary.300}'
                },
                highlight: {
                    background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
                    focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
                    color: 'rgba(255,255,255,.87)',
                    focusColor: 'rgba(255,255,255,.87)'
                },
                surface: BDP_SURFACE_DARK
            }
        }
    },
    components: {
        /* "Secondary buttons ... Chosen buttons should be full gray #3e5172 with
           white text. Unchosen should be white with the same gray outline." */
        button: {
            colorScheme: {
                light: {
                    secondary: {
                        background: '#3e5172',
                        hoverBackground: '#2e3c54',
                        activeBackground: '#2c3a52',
                        borderColor: '#3e5172',
                        hoverBorderColor: '#2e3c54',
                        activeBorderColor: '#2c3a52',
                        color: '#ffffff',
                        hoverColor: '#ffffff',
                        activeColor: '#ffffff'
                    },
                    outlined: {
                        secondary: {
                            borderColor: '#3e5172',
                            color: '#000000',
                            hoverBackground: 'color-mix(in srgb, #3e5172, transparent 92%)',
                            activeBackground: 'color-mix(in srgb, #3e5172, transparent 84%)'
                        }
                    }
                }
            }
        },
        /* "Selected Tab should be Bright Blue" with no tab background colour. */
        tabs: {
            tab: {
                background: 'transparent',
                activeBackground: 'transparent',
                hoverBackground: 'transparent',
                activeColor: '{primary.color}',
                activeBorderColor: '{primary.color}'
            },
            tablist: {
                background: 'transparent'
            }
        },
        /* "Table banners should be white, with black headline text." */
        datatable: {
            colorScheme: {
                light: {
                    header: {
                        background: '#ffffff',
                        color: '#000000'
                    }
                }
            }
        }
    }
});

export const appConfig: ApplicationConfig = {
    providers: [
        MessageService,
        ConfirmationService,
        provideRouter(
            appRoutes, withHashLocation(),
            withInMemoryScrolling({
                anchorScrolling: 'enabled',
                scrollPositionRestoration: 'top'
            }),
            withEnabledBlockingInitialNavigation()
        ),
        provideHttpClient(withFetch(), withInterceptors([apiInterceptor])),
        provideAnimationsAsync(),
        providePrimeNG({ theme: { preset: MyPreset, options: { darkModeSelector: '.app-dark' } } }),
        provideMonacoEditor()
    ]
};
